import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../api/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Loader2 } from 'lucide-react';

const SurveyPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const { refreshUser } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    years_of_experience: '',
    location_of_practice: '',
  });

  const handleValueChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.years_of_experience || !formData.location_of_practice.trim()) {
      alert('Please answer both questions.');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        years_of_experience: formData.years_of_experience,
        location_of_practice: formData.location_of_practice.trim(),
      };

      await apiClient.post('/api/auth/survey', payload);
      await refreshUser();

      const response = await apiClient.get('/api/auth/next-task');
      const { redirect, patient_id } = response.data;

      if (redirect === 'chart') {
        navigate(`/patient-chart/${patient_id}`);
      } else if (redirect === 'group_c_selection') {
        navigate('/group-c-selection');
      } else {
        navigate('/patient-chart/patient_0');
      }
    } catch (error) {
      console.error('Failed to submit survey:', error);
      alert('Failed to submit survey. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center py-12 px-4">
      <div className="w-full max-w-2xl space-y-8">
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold text-gray-900">Background Survey</h1>
          <div className="h-1 w-24 bg-blue-500 rounded-full mx-auto mb-6"></div>
          <p className="text-gray-600">
            Please complete this short 2-question survey before starting the study.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Professional Background</CardTitle>
            <CardDescription>Both fields are required.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-8">
              <div className="space-y-3">
                <Label className="text-base font-semibold">1. How many years of professional experience do you have in your current role?</Label>
                <Select onValueChange={(val) => handleValueChange('years_of_experience', val)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select years of experience" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Student / trainee">Student / trainee</SelectItem>
                    <SelectItem value="Less than 1 year">Less than 1 year</SelectItem>
                    <SelectItem value="1-2 years">1-2 years</SelectItem>
                    <SelectItem value="3-5 years">3-5 years</SelectItem>
                    <SelectItem value="6-10 years">6-10 years</SelectItem>
                    <SelectItem value="11-20 years">11-20 years</SelectItem>
                    <SelectItem value="More than 20 years">More than 20 years</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3">
                <Label className="text-base font-semibold">2. Location of Practice (City/Country)</Label>
                <Input
                  placeholder="e.g., Chicago, USA"
                  value={formData.location_of_practice}
                  onChange={(e) => handleValueChange('location_of_practice', e.target.value)}
                />
              </div>

              <Button type="submit" className="w-full text-lg py-6" disabled={loading}>
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Submit Survey
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SurveyPage;
